import { FormEvent, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useUpdateMe } from '../hooks/useAuth'
import { track } from '../lib/analytics'
import { reverseGeocode } from '../lib/geocode'
import { Wordmark } from '../components/ui/Wordmark'
import { EVENT_TYPES, EventTypeKey } from '../components/ui/eventTypes'
import { MapPin, Check, ArrowRight } from 'lucide-react'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'

const TYPE_COPY: Record<EventTypeKey, string> = {
  potluck:  'Bring a dish, share a meal',
  worship:  'Prayer, song, revival',
  study:    'Group study, every week',
  groups:   'Small groups, life groups',
  social:   'Game nights, mixers',
  outdoors: 'Trails, hikes, pickup games',
  kids:     'VBS, youth group, camp',
  service:  'Volunteer together',
}


type Step = 'denomination' | 'location' | 'interests'
const STEP_ORDER: Step[] = ['denomination', 'location', 'interests']

const GRADIENT = 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'

function PageShell({ step, onSkip, children }: { step: Step; onSkip: () => void; children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', fontFamily: '"Outfit", system-ui, sans-serif' }}>
      <TopBar current={step} onSkip={onSkip} />
      <div className="onboarding-grid">
        {children}
      </div>
    </div>
  )
}

function TopBar({ current, onSkip }: { current: Step; onSkip: () => void }) {
  const idx = STEP_ORDER.indexOf(current)
  return (
    <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--line-soft)', flexWrap: 'wrap', gap: 8 }}>
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Wordmark size={20} />
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em' }}>
          STEP {idx + 1} OF 3
        </div>
        {/* Progress bar */}
        <div style={{ width: 120, height: 4, borderRadius: 999, background: 'var(--line)', overflow: 'hidden' }}>
          <div style={{ width: `${((idx + 1) / 3) * 100}%`, height: '100%', background: GRADIENT, borderRadius: 999, transition: 'width 0.3s ease' }} />
        </div>
        <button onClick={onSkip} style={{ background: 'transparent', border: 0, fontSize: 13, color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' }}>
          Skip setup
        </button>
      </div>
    </header>
  )
}

export default function OnboardingPage() {
  const { user, setOnboardingComplete } = useAuthStore()
  const updateMe = useUpdateMe()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('denomination')
  const [locationLabel, setLocationLabel] = useState(user?.locationLabel ?? '')
  const [locationError, setLocationError] = useState<string | undefined>()
  const [geoLoading, setGeoLoading] = useState(false)
  const [picked, setPicked] = useState<EventTypeKey[]>([])

  function finish(skipped = false) {
    setOnboardingComplete()
    track(skipped ? 'onboarding_skipped' : 'onboarding_complete')
    navigate('/app')
  }

  function toggleType(k: EventTypeKey) {
    setPicked(picked.includes(k) ? picked.filter(x => x !== k) : [...picked, k])
  }

  function useMyLocation() {
    if (!navigator.geolocation) { setLocationError('Geolocation is not supported by your browser'); return }
    setGeoLoading(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        const label = await reverseGeocode(latitude, longitude) ?? 'My location'
        setGeoLoading(false)
        updateMe.mutate(
          { locationLat: latitude, locationLng: longitude, locationLabel: label },
          { onSuccess: () => setStep('interests') },
        )
      },
      () => {
        setGeoLoading(false)
        setLocationError('Could not get your location. Please type it instead.')
      },
      { timeout: 8000 },
    )
  }

  function handleLocationSubmit(e: FormEvent) {
    e.preventDefault()
    setLocationError(undefined)
    if (!locationLabel.trim()) { setLocationError('Please enter your city or address'); return }
    updateMe.mutate({ locationLabel: locationLabel.trim() }, { onSuccess: () => setStep('interests') })
  }

  // ── Step 1: Denomination ─────────────────────────────────────────────────────
  if (step === 'denomination') {
    return (
      <PageShell step={step} onSkip={() => finish(true)}>
        <div>
          <h1 className="onboarding-heading" style={{ margin: '0 0 20px', fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.025em', color: 'var(--ink)' }}>
            Which tradition<br />
            <span className="serif-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              are you part of?
            </span>
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0, maxWidth: 400 }}>
            We use this to personalise event names, suggestions, and your weekly highlights.
          </p>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 20, padding: '24px', boxShadow: '0 1px 2px rgba(31,26,46,.04), 0 12px 28px rgba(31,26,46,.06)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {DENOMINATION_OPTIONS.map((d) => (
              <button
                key={d.key}
                onClick={() => updateMe.mutate({ denomination: d.key }, { onSuccess: () => setStep('location') })}
                disabled={updateMe.isPending}
                style={{ padding: '10px 14px', background: 'var(--bg)', border: '1.5px solid var(--line)', borderRadius: 10, fontSize: 13, fontWeight: 500, color: 'var(--ink)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.12s' }}
              >
                {d.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setStep('location')}
            style={{ width: '100%', padding: '12px 14px', background: 'transparent', border: '1.5px solid var(--line-soft)', borderRadius: 10, fontSize: 13, fontWeight: 400, color: 'var(--muted)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}
          >
            Not sure yet
          </button>
        </div>
      </PageShell>
    )
  }

  // ── Step 2: Location ─────────────────────────────────────────────────────────
  if (step === 'location') {
    return (
      <PageShell step={step} onSkip={() => finish(true)}>
        <div>
          <h1 className="onboarding-heading" style={{ margin: '0 0 20px', fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.025em', color: 'var(--ink)' }}>
            Where are you<br />
            <span className="serif-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              located?
            </span>
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0, maxWidth: 400 }}>
            We use this to show events near you. You can update it anytime.
          </p>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 20, padding: '36px 32px', boxShadow: '0 1px 2px rgba(31,26,46,.04), 0 12px 28px rgba(31,26,46,.06)' }}>
          <button
            type="button"
            onClick={useMyLocation}
            disabled={geoLoading || updateMe.isPending}
            style={{ width: '100%', height: 48, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'transparent', border: '1.5px solid var(--line)', borderRadius: 999, fontSize: 14, fontWeight: 500, cursor: 'pointer', color: 'var(--ink)', fontFamily: 'inherit' }}
          >
            <MapPin size={16} color="var(--primary)" />
            {geoLoading ? 'Getting location…' : 'Use my current location'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            <span style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
          </div>

          <form onSubmit={handleLocationSubmit} noValidate>
            <label htmlFor="location" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
              City or address
            </label>
            <input
              className="input"
              id="location"
              type="text"
              placeholder="e.g. Loma Linda, CA"
              value={locationLabel}
              onChange={(e) => setLocationLabel(e.target.value)}
              style={{ marginBottom: locationError ? 6 : 20 }}
              aria-describedby={locationError ? 'location-error' : undefined}
              aria-invalid={Boolean(locationError)}
            />
            {locationError && (
              <p id="location-error" style={{ fontSize: 12, color: '#b45309', margin: '0 0 16px' }}>{locationError}</p>
            )}
            <button type="submit" disabled={updateMe.isPending} className="btn btn--full btn--lg">
              {updateMe.isPending ? 'Saving…' : 'Continue'} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </PageShell>
    )
  }

  // ── Step 3: Interests ────────────────────────────────────────────────────────
  return (
    <PageShell step={step} onSkip={() => finish(true)}>
      <div>
        <h1 className="onboarding-heading" style={{ margin: '0 0 20px', fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.025em', color: 'var(--ink)' }}>
          What kind of<br />
          <span className="serif-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            fellowship
          </span>
          <br />are you looking for?
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)', margin: '0 0 28px', maxWidth: 400 }}>
          Pick a few and we'll show those first. You can change anytime.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => finish()} className="btn btn--lg" style={{ gap: 8 }}>
            {picked.length === 0 ? 'See all events' : `See ${picked.length} type${picked.length === 1 ? '' : 's'}`}
            <ArrowRight size={16} />
          </button>
          <button onClick={() => finish()} style={{ background: 'transparent', border: 0, fontSize: 13.5, color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' }}>
            Skip for now
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {(Object.entries(EVENT_TYPES) as [EventTypeKey, typeof EVENT_TYPES[EventTypeKey]][]).map(([id, t]) => {
          const on = picked.includes(id)
          return (
            <button
              key={id}
              onClick={() => toggleType(id)}
              style={{
                padding: '20px', borderRadius: 18, cursor: 'pointer', textAlign: 'left',
                display: 'flex', flexDirection: 'column', gap: 12,
                background: on ? t.tint : 'var(--surface)',
                border: on ? `2px solid ${t.color}` : '1.5px solid var(--line-soft)',
                position: 'relative', fontFamily: 'inherit', transition: 'all 0.12s',
              }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 10, background: on ? t.color : t.tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <t.icon size={18} color={on ? '#fff' : t.color} strokeWidth={2} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>{t.label}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>{TYPE_COPY[id]}</div>
              </div>
              {on && (
                <span style={{ position: 'absolute', top: 14, right: 14, width: 24, height: 24, borderRadius: '50%', background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Check size={13} color="#fff" strokeWidth={2.5} />
                </span>
              )}
            </button>
          )
        })}
      </div>
    </PageShell>
  )
}
